import { getFilterKey, isOptionChecked } from './utils';
import { Button, Checkbox, Typography } from '@navi/web-ui/lib/primitives';
import styles from './index.module.scss';
import cx from 'classnames';
import { ISectionedOptionValues } from './types';
import FilterRenderingEngine from './FilterRenderingEngine';
import {
  FILTERS_WITH_SUBFILTERS,
  FiltersWithSubfiltersMapping,
  subFilterMapping
} from './constants';
import { RadioGroup } from '@navi/web-ui/lib/components';
import { ChangeEvent } from 'react';
import { RadioOptions } from '@navi/web-ui/lib/components/RadioGroup/types';

const SectionedOptions = (props: ISectionedOptionValues) => {
  const {
    filteredOptionsData,
    filterSchema,
    selectedOptions,
    selectedFilter,
    handleCheckboxChange,
    setSelectedOptions,
    isSearchEnabled
  } = props;

  const showResetButton = filterSchema?.[selectedFilter]?.showResetButton;
  const isMultiSelect = filterSchema?.[selectedFilter]?.isMultiSelect;
  const nestedFilter = filterSchema?.[selectedFilter]?.nestedFilter;

  const resetSelectedFilter = () => {
    if (nestedFilter) {
      const updatedOptions = { ...selectedOptions };
      filteredOptionsData.forEach(option => {
        const { filterSectionKey } = option;
        if (!filterSectionKey) return;
        updatedOptions[selectedFilter] = {
          ...updatedOptions[selectedFilter],
          [filterSectionKey]: []
        };
      });
      setSelectedOptions(updatedOptions);
    } else {
      setSelectedOptions({
        ...selectedOptions,
        [selectedFilter]: []
      });
    }
  };

  const handleSingleSelectChange = (
    e: ChangeEvent<HTMLInputElement>,
    filterSectionKey?: string
  ) => {
    const value = e.target.value;
    if (nestedFilter && filterSectionKey) {
      setSelectedOptions({
        ...selectedOptions,
        [selectedFilter]: {
          ...selectedOptions[selectedFilter],
          [filterSectionKey]: [value]
        }
      });
    } else {
      setSelectedOptions({ ...selectedOptions, [selectedFilter]: [value] });
    }
  };

  const getSingleFilterValue = (filterSectionKey?: string) => {
    const selectedOption = selectedOptions[selectedFilter];
    if (nestedFilter && filterSectionKey) {
      return selectedOption?.[filterSectionKey]?.[0] || '';
    } else {
      return selectedOption?.[0] || '';
    }
  };

  const checkIfDisabled = (disabledUntilSelected?: string) => {
    if (!disabledUntilSelected) {
      return false;
    }
    const selectedOption = selectedOptions[selectedFilter];
    return !selectedOption?.[disabledUntilSelected]?.[0];
  };

  return (
    <div className={cx(styles.filteredOptions, { [styles.noSearch]: !isSearchEnabled }, '!pt-0')}>
      {showResetButton ? (
        <Button
          variant="text"
          className="flex justify-end cursor-pointer px-2 py-1 w-full"
          onClick={resetSelectedFilter}
        >
          Reset
        </Button>
      ) : null}
      {filteredOptionsData.map(option => {
        const { filterSectionLabel, filterSectionValue, filterSectionKey, disabledUntilSelected } =
          option;

        return (
          <div key={filterSectionLabel}>
            <Typography variant="p3" className={styles.filterSectionLabel} as="div">
              {filterSectionLabel}
            </Typography>
            {isMultiSelect ? (
              filterSectionValue.map(option => {
                const isChecked = isOptionChecked(
                  option?.value as string,
                  selectedOptions,
                  selectedFilter
                );
                return (
                  <div className={styles.option} key={option.label}>
                    <Checkbox
                      onChange={e => handleCheckboxChange(e, option)}
                      title={option.label as string}
                      defaultChecked={isChecked}
                    />
                    {isChecked &&
                    FILTERS_WITH_SUBFILTERS.includes(
                      option?.value as FiltersWithSubfiltersMapping
                    ) ? (
                      <FilterRenderingEngine
                        filterType={
                          subFilterMapping.get(option?.value as FiltersWithSubfiltersMapping)!
                        }
                        filterKey={getFilterKey(selectedFilter, option?.value as string)!}
                        filterSchema={filterSchema}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                      />
                    ) : null}
                  </div>
                );
              })
            ) : (
              <div
                className={cx(styles.option, {
                  'opacity-70': checkIfDisabled(disabledUntilSelected)
                })}
              >
                <RadioGroup
                  direction="column"
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleSingleSelectChange(e, filterSectionKey)
                  }
                  value={getSingleFilterValue(filterSectionKey)}
                  name={filterSectionKey}
                  options={filterSectionValue as RadioOptions[]}
                  disabled={checkIfDisabled(disabledUntilSelected)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SectionedOptions;
